'use client'

import { sendKlaviyoViewCardEvent } from '@/app/api/klaviyo-event'
import {
	emitHotelCardClickGAEvent,
	emitRpNoSpecificHotelSeeAvailabilityGAEvent,
} from '@/app/ga-event-helpers'
import { submitHiddenForm } from '@/app/utils'
import { useLocalStorage } from '@uidotdev/usehooks'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { FC, MouseEvent, PropsWithChildren, useMemo, useState } from 'react'
import { v5 as uuid } from 'uuid'
import {
	HOTELS_LOCAL_STORAGE_KEY,
	INIT_DATA_STORAGE_KEY,
	MAX_HOTELS_LOCAL_STORAGE_LENGTH,
	REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY,
	UUID_NAMESPACE,
} from '../constants'
import {
	HotelItem,
	HotelItemLocalStorage,
	HotelsListResponse,
	UserData,
} from '../types'
import RedirectCaller from './RedirectCaller'
export interface HotelListItemClickProps extends PropsWithChildren {
	item: HotelItem
	passthruLink: string
	link: string
	provider: string
	searchParams: HotelsListResponse['searchParams']
	isAdditionalPrice?: boolean
	onMouseOver?: () => void
	onMouseLeave?: () => void
	price: number
	event?: string
}

const HotelListItemClick: FC<HotelListItemClickProps> = ({
	item,
	passthruLink,
	link,
	onMouseOver,
	onMouseLeave,
	provider,
	searchParams,
	isAdditionalPrice,
	price,
	event,
	children,
}) => {
	const pathname = usePathname()
	const [formData, __] = useLocalStorage<Record<string, any>>(
		REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY
	)
	const [hotels, setHotels] = useLocalStorage<HotelItemLocalStorage[]>(
		HOTELS_LOCAL_STORAGE_KEY,
		[]
	)

	const [userData, setUserData] = useLocalStorage<UserData>(
		INIT_DATA_STORAGE_KEY
	)

	const [redirecting, setRedirecting] = useState(false)
	const [windowRef, setWindowRef] = useState<Window | null>(null)

	const hotelHasAvailability = item.rawHotelData.bestRate.availableRooms > 0

	const hotelItem = useMemo(() => { 
		const additionalPricePartner = isAdditionalPrice ? provider : null
		return {
			...item,
			searchParams,
			passthruLink,
			link,
			provider,
			additionalPrice: !!additionalPricePartner,
			price: String(price),
			id: uuid(link, UUID_NAMESPACE),
		} as HotelItemLocalStorage
	}, [isAdditionalPrice, item, link, passthruLink, price, provider, searchParams])

	function setItemToLocalStorage() {
		if (hotels.length > MAX_HOTELS_LOCAL_STORAGE_LENGTH) {
			hotels.shift()
		}
		hotels.push(hotelItem)
		setHotels(hotels)
	}

	function redirectInPlace() {
		if (redirecting) return;
		setWindowRef(window.open())
		setRedirecting(true);
	}
	
	function onRedirect() {
		setRedirecting(false)
	}

	function onClick(e: MouseEvent) {
		const additionalPricePartner = isAdditionalPrice ? provider : null

		emitHotelCardClickGAEvent({
			name: item.title,
			location: formData['location-name'],
			rating: item.rating,
			stars: `${item.stars} stars`,
			bestPrice: item.bestPriceOn,
			additionalPricePartner,
		})

		if (userData.email) {
			sendKlaviyoViewCardEvent({
				item: item,
				email: userData.email,
				pathname,
			})
		}

		const redirectForm = document.getElementById('redirect-form_blank')
		if (hotelHasAvailability) {
			setItemToLocalStorage()
			e.preventDefault()
			redirectInPlace()
		} else {
			submitHiddenForm(formData, redirectForm as HTMLFormElement)
			emitRpNoSpecificHotelSeeAvailabilityGAEvent()
		}
	}

	function onHover() {
		setItemToLocalStorage()
		onMouseOver?.()
	}

	if (children) {
		return (
			<Link
				aria-label="Book now"
				target="_blank"
				href={passthruLink}
				onClick={onClick}
				onMouseEnter={onHover}
				onMouseLeave={onMouseLeave}
			>
				{children}
			</Link>
		)
	}

	return hotelHasAvailability ? (
		<>
			<Link
				className="hotels-list-click"
				aria-label="Book now"
				target="_blank"
				href={passthruLink}
				onClick={onClick}
				onMouseEnter={onHover}
				onMouseLeave={onMouseLeave}
			/>
			{(redirecting && <RedirectCaller hotelItem={hotelItem} redirectInPlace windowRef={windowRef} onRedirect={onRedirect} />)}
		</>
	) : (
		<div className="hotels-list-click" onClick={onClick} />
	)
}

export default dynamic(() => Promise.resolve(HotelListItemClick), {
	ssr: false,
})